import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, ChevronRight, Search, Calendar, RefreshCw, FilterX, Filter,
  Tag, Users, X, TrendingUp, TrendingDown, Download 
} from 'lucide-react';
import { 
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
  PieChart, Pie, Cell
} from 'recharts';
import './DashboardBultos.css';
import SellOutDashboardMenuHeader from '../components/SellOutDashboardMenuHeader';
import { useQuery } from '../QueryContext';
import { clearCache } from '../utils/dashboardCache';
import { useAuth } from '../AuthContext';
import * as XLSX from 'xlsx';
import FullScreenLoader from './FullScreenLoader';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

// --- HELPERS ---
const formatNumber = (num) => new Intl.NumberFormat('de-DE').format(Math.round(num || 0));
const formatK = (num) => (num >= 1000 || num <= -1000) ? `${(num / 1000).toFixed(1)}K` : Math.round(num || 0);

const COLORS = ['#1a2c4e', '#0068b3', '#00ba52', '#4f8cff', '#00e6b8', '#334767'];

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.03) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 12; 
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="#cbd5e1" fontSize="10" fontWeight="600" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const filterLabels = {
  ST_CLIENTE_NOMBRE: "Cliente",
  ST_CLI_PRIN: "Cliente Principal",
  ST_CANAL: "Canal",
  ST_REGION: "Región",
  ST_VENDEDOR: "Vendedor",
  ST_PROVEEDOR: "Proveedor",
  ST_MARCA: "Marca",
  ST_LINEA: "Línea",
  ST_CLASIF2: "Clasificación",
  ST_SEGMENTO: "Segmento",
  ST_SKU_NOMBRE: "Producto"
};

const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const INITIAL_FILTERS = {
  ST_CLIENTE_NOMBRE: [], ST_CLI_PRIN: [], ST_CANAL: [], ST_REGION: [],
  ST_VENDEDOR: [], ST_PROVEEDOR: [], ST_MARCA: [],
  ST_LINEA: [], ST_CLASIF2: [], ST_SEGMENTO: [], ST_SKU_NOMBRE: []
};

// --- COMPONENTE: GlobalSearch (Específico para Sell Out Tradicional) ---
const GlobalSearch = ({ availableOptions, filterLabels, filters, setFilters }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);

  const allOptions = useMemo(() => {
    return Object.entries(availableOptions || {}).flatMap(([key, options]) =>
      (options || []).map(value => ({
        key,
        label: filterLabels[key] || key,
        value: String(value).trim()
      }))
    );
  }, [availableOptions, filterLabels]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length > 1) {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = allOptions
          .filter(opt => opt.value.toLowerCase().includes(lowerSearch))
          .slice(0, 20);
        setSuggestions(filtered);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, allOptions]);



  const handleSelect = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: (prev[key] || []).includes(value) ? prev[key] : [...(prev[key] || []), value]
    }));
    setSearchTerm('');
    setShowDropdown(false);
  }, [setFilters]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      if (selectedIndex >= 0) {
        handleSelect(suggestions[selectedIndex].key, suggestions[selectedIndex].value);
      } else if (suggestions.length > 0) {
        handleSelect(suggestions[0].key, suggestions[0].value);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSearchTerm('');
    }
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={i} style={{background: '#ffeb3b', color: '#000', padding: '0 2px', borderRadius: '2px'}}>{part}</mark>
        : part
    );
  };

  return (
    <div className="global-search-container" ref={dropdownRef}>
      <div className="global-search-input-wrapper">
        <Search size={16} className="global-search-icon" />
        <input
          type="text"
          className="global-search-input"
          placeholder="Buscar cliente, marca..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {searchTerm && <X size={14} className="clear-search" style={{cursor:'pointer', opacity:0.5, marginRight: '8px'}} onClick={() => setSearchTerm('')} />}
      </div>

      <AnimatePresence>
        {showDropdown && suggestions.length > 0 && (
          <motion.div className="global-search-dropdown" initial={{opacity:0, y:-10}} animate={{opacity:1, y:0}} exit={{opacity:0}}>
            <div className="suggestions-list" transition={isMobile ? { duration: 0.1 } : { duration: 0.2 }}>
              {suggestions.map((opt, i) => (
                <div 
                  key={`${opt.key}-${i}`} 
                  className={`suggestion-item ${i === selectedIndex ? 'selected' : ''}`} 
                  onClick={() => handleSelect(opt.key, opt.value)}
                >
                  <span className="suggestion-category">{opt.label}:</span>
                  <span className="suggestion-value">
                    {highlightMatch(opt.value, searchTerm)}
                  </span>
                  {(filters[opt.key] || []).includes(opt.value) && <span className="suggestion-active" style={{color: '#00ba52', marginLeft: 'auto'}}>✓</span>}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- COMPONENTE: MultiSelectFilter ---
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = (options || []).filter(opt => String(opt).toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 100);

  const areAllSelected = filteredOptions.length > 0 && filteredOptions.every(opt => (selectedValues || []).includes(opt));

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = (selectedValues || []).filter(val => !filteredOptions.includes(val));
      onChange(name, newSelected);
    } else {
      const newSelected = [...new Set([...(selectedValues || []), ...filteredOptions])];
      onChange(name, newSelected);
    }
  };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button className={`filter-pill-button ${(selectedValues || []).length > 0 ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{(selectedValues || []).length === 0 ? 'Todos' : (selectedValues.length === 1 ? selectedValues[0] : `${selectedValues.length} Sel.`)}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={isMobile ? { duration: 0.1 } : { duration: 0.2 }} className="filter-dropdown-panel with-search">
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
            </div>
            {filteredOptions.length > 0 && (
              <label className="checkbox-row select-all-row">
                <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
                <span>Seleccionar Todos</span>
              </label>
            )}
            <div className="options-list">
              {filteredOptions.map((opt, i) => (
                <label key={i} className="checkbox-row">
                  <input type="checkbox" checked={(selectedValues || []).includes(opt)} onChange={() => {
                    const currentVals = selectedValues || [];
                    const newVals = currentVals.includes(opt) ? currentVals.filter(v => v !== opt) : [...currentVals, opt];
                    onChange(name, newVals);
                  }} />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
            {(selectedValues || []).length > 0 && <button className="reset-pill-link" onClick={() => onChange(name, [])}>Limpiar</button>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---
const SellOutTradicional = () => {
  const { sotradQuery, getSotradFilters } = useQuery();
  const [isLocalLoading, setIsLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [unitMode, setUnitMode] = useState('bultos');
  const [metrics, setMetrics] = useState({ sellout: 0, sellin: 0, efectividad: 0, var_year: 0, var_month: 0, var_efec_year: 0 });
  const [chartData, setChartData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [expandedClients, setExpandedClients] = useState({});
  const [breakdownType] = useState('VENDEDOR');
  const [monthlyTableMeses] = useState(() => {
    const d = new Date();
    return nombresMeses.slice(0, d.getMonth() + 1);
  });
  const [monthlyAdvisorData, setMonthlyAdvisorData] = useState([]);
  const [expandedBrands, setExpandedBrands] = useState({});
  
  const [availableOptions, setAvailableOptions] = useState({});
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [selectedMeses, setSelectedMeses] = useState([nombresMeses[new Date().getMonth()]]);
  const [selectedAnios, setSelectedAnios] = useState([new Date().getFullYear()]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const { session } = useAuth();
  const filtersInitialized = useRef(false);

  useEffect(() => {
    const loadFilters = async () => {
      setIsLocalLoading(true);
      try {
        const config = await getSotradFilters();
        console.log("DEBUG: SellOutTrad - Config received:", config);
        if (!config) return;

        const baseOptions = config.availableOptions || {};
        setAvailableOptions(baseOptions);
        
        // ✅ Inicialización inteligente de fechas (Igual que SellOutMod)
        if (config.availableYears && config.availableYears.length > 0) {
          setSelectedAnios([Number(config.currentYear) || config.availableYears[0]]);
        } else {
          setSelectedAnios([new Date().getFullYear()]); // Fallback if no years from config
        }
        if (config.availableMeses && config.availableMeses.length > 0) {
          const currentMonthName = nombresMeses[new Date().getMonth()].toUpperCase();
          const defaultMonth = config.availableMeses.find(m => m.toUpperCase() === currentMonthName) 
                               || config.availableMeses[config.availableMeses.length - 1];
          setSelectedMeses([nombresMeses.find(m => m.toUpperCase() === defaultMonth.toUpperCase()) || defaultMonth]);
        } else {
          setSelectedMeses([nombresMeses[new Date().getMonth()]]); // Fallback if no months from config
        }

        filtersInitialized.current = true;
      } catch (err) { 
        console.error('Error loading SOTrad config:', err); 
      } finally { 
        setIsLocalLoading(false); 
      }
    };
    loadFilters();
  }, [getSotradFilters]);

  const calculateMetrics = useCallback(async () => {
    if (!filtersInitialized.current) return;
    setQuerying(true);
    try {
      const payload = {
        report_type: 'summary', // 🎯 FIX CRÍTICO: Forzar siempre summary aquí para que devuelva KPIs, Pie y Tabla
        metric_type: unitMode === 'unidades' ? 'UNIDADES' : 'BULTOS',
        page_meses: selectedMeses,
        page_anios: selectedAnios,
        filters: filters,
        session: session
      };
      console.log("DEBUG: SellOutTrad - calculateMetrics Payload (Summary):", payload);

      const [summaryResponse, trendResponse] = await Promise.all([
        sotradQuery(payload),
        sotradQuery({ ...payload, page_meses: nombresMeses })
      ]);
      
      if (summaryResponse) {
        // 1. Procesar KPIs
        if (summaryResponse.kpis_sot) {
          const k = Array.isArray(summaryResponse.kpis_sot) ? summaryResponse.kpis_sot[0] : summaryResponse.kpis_sot;
          setMetrics({
            sellout: k.sellout || k.v_actual || 0,
            sellin: k.sellin || k.q_actual || 0,
            efectividad: k.efectividad || k.porcentaje || 0,
            var_year: k.var_year || k.var_v_anual || 0,
            var_month: k.var_month || k.var_v_mensual || 0,
            var_efec_year: k.var_efec_year || k.var_p_anual || 0
          });
        }

        // 3. Pie Chart (Torta)
        if (summaryResponse.pie) {
          setPieData(summaryResponse.pie);
        }

        // 4. Formateo de Tabla Jerárquica (Clientes -> Marcas -> Productos)
        if (summaryResponse.table_sot && Array.isArray(summaryResponse.table_sot)) {
          const formatHierarchicalData = (flatData) => {
            const clientMap = {};
            flatData.forEach(row => {
              const cName = row.client_name || "SIN CLIENTE";
              const bName = row.brand_name || "SIN MARCA";
              
              if (!clientMap[cName]) {
                clientMap[cName] = { name: cName, brands: {}, si: 0, so: 0 };
              }
              if (!clientMap[cName].brands[bName]) {
                clientMap[cName].brands[bName] = { name: bName, skus: [], si: 0, so: 0 };
              }

              const skuSo = Number(row.sellout || 0);
              const skuSi = Number(row.sellin || 0);

              clientMap[cName].brands[bName].skus.push({
                name: row.sku_name || "PRODUCTO DESCONOCIDO",
                si: skuSi,
                so: skuSo,
                efectividad: skuSi > 0 ? (skuSo / skuSi) * 100 : 0
              });

              clientMap[cName].brands[bName].si += skuSi;
              clientMap[cName].brands[bName].so += skuSo;
              clientMap[cName].si += skuSi;
              clientMap[cName].so += skuSo;
            });

            return Object.values(clientMap).map(client => {
              client.brands = Object.values(client.brands).map(brand => {
                brand.efectividad = brand.si > 0 ? (brand.so / brand.si) * 100 : 0;
                return brand;
              });
              client.efectividad = client.si > 0 ? (client.so / client.si) * 100 : 0;
              return client;
            });
          };

          setTableData(formatHierarchicalData(summaryResponse.table_sot));
        }
      }
      // 2. Trend (Barras) - Usar específicamente la respuesta de tendencia
      if (trendResponse && trendResponse.trend) {
          setChartData(trendResponse.trend);
        }

    } catch (err) { 
      console.error("Error fetching SOTrad metrics:", err); 
    } finally { 
      setQuerying(false); 
    }  
  }, [filters, selectedMeses, selectedAnios, unitMode, sotradQuery, session]);


  const calculateMonthlyAdvisorMetrics = useCallback(async () => {
    if (!filtersInitialized.current) return;
    try {
      const payload = {
        report_type: 'monthly_advisor', // Este se mantiene exclusivo para la tabla mensual
        metric_type: unitMode === 'unidades' ? 'UNIDADES' : 'BULTOS',
        breakdown_type: breakdownType,
        page_meses: monthlyTableMeses,
        page_anios: selectedAnios,
        filters,
        session
      };
      console.log("DEBUG: SellOutTrad - calculateMonthlyAdvisorMetrics Payload:", payload);

      const response = await sotradQuery(payload);
      console.log("Monthly response:", response);

      setMonthlyAdvisorData(response?.data ?? []);
    } catch (err) {
      console.error("Error calculating monthly metrics:", err);
      setMonthlyAdvisorData([]);
    }
  }, [filters, selectedAnios, session, breakdownType, monthlyTableMeses, unitMode, sotradQuery]); // nombresMeses es constante

  useEffect(() => {
    const timer = setTimeout(calculateMetrics, 300);
    return () => clearTimeout(timer);
  }, [calculateMetrics]);

  useEffect(() => {
    const timer = setTimeout(calculateMonthlyAdvisorMetrics, 300);
    return () => clearTimeout(timer);
  }, [calculateMonthlyAdvisorMetrics]);

  const handleFilterChange = (name, values) => setFilters(prev => ({ ...prev, [name]: values }));
  const resetFilters = () => setFilters(INITIAL_FILTERS);
  
  // ✅ Refresh silencioso (Solo animación de botón)
  const handleRefresh = async () => {
  clearCache();
  await Promise.all([
    calculateMetrics(),
    calculateMonthlyAdvisorMetrics()
  ]);
};

  const handleExportExcel = (type = 'standard') => {
    if (tableData.length === 0) return;
    
    if (type === 'monthly') {
      const rows = [];
      const entityLabel = breakdownType;
      const sortedM = [...monthlyTableMeses].sort((a, b) => nombresMeses.indexOf(a) - nombresMeses.indexOf(b));
      
      monthlyAdvisorData.forEach(adv => {
        const row = { [entityLabel]: adv.entidad };
        sortedM.forEach(mesName => {
          const m = adv.months[nombresMeses.indexOf(mesName)] || { venta: 0, cuota: 0, logro: 0 };
          row[`${mesName}_SellIn`] = Math.round(m.cuota);
          row[`${mesName}_SellOut`] = Math.round(m.venta);
          row[`${mesName}_Efec`] = Math.round(m.logro) + '%';
        });
        row.Total_SellOut = Math.round(adv.totalReal);
        rows.push(row);
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Mensual");
      XLSX.writeFile(wb, `SellOut_Tradicional_Mensual_${new Date().getTime()}.xlsx`);
      return;
    }

    const rows = [];
    tableData.forEach(client => {
      client.brands.forEach(brand => {
        brand.skus.forEach(sku => {
          rows.push({
            Cliente: client.name,
            Marca: brand.name,
            Producto: sku.name,
            Sell_In: Math.round(sku.si || 0),
            Sell_Out: Math.round(sku.so || 0),
            Efectividad_Pct: Math.round(sku.efectividad || 0)
          });
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SellOut");
    XLSX.writeFile(wb, `SellOut_Tradicional_${new Date().getTime()}.xlsx`);
  };

  const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', border: '1px solid #ccc' }}>
          <p style={{ color: '#333', fontWeight: 'bold', marginBottom: '5px' }}>{label}</p>
          <p style={{ color: '#0068b3', fontSize: '0.8rem', margin: 0 }}>Sell Out: {formatNumber(payload[0].value)}</p>
          <p style={{ color: '#00ba52', fontSize: '0.8rem', margin: 0 }}>Sell In: {formatNumber(payload[1].value)}</p>
        </div>
      );
    }
    return null;
  };

  // Mostrar FullScreenLoader solo si es la carga inicial y los filtros no se han inicializado
  if (isLocalLoading && !filtersInitialized.current) return <FullScreenLoader message="Consultando API de Sell-Out Tradicional..." />;

  return (
    <motion.div className="dashboard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <SellOutDashboardMenuHeader />
      
      <header className="header-flex">
        <div className="title-section">
          <h1 className="dashboard-title">Sell Out Tradicional</h1>
          <div className="title-underline"></div>
        </div>
        <div className="date-selectors">
          <button className="refresh-button" onClick={handleRefresh} disabled={querying} title="Actualizar Datos">
            <RefreshCw size={16} className={querying ? 'spin-anim' : ''} />
          </button>
          <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
            <Filter size={16} />
            <span>Filtros</span>
          </button>
          <div className="switch-toggle-group">
            <button className={`switch-toggle-btn ${unitMode === 'bultos' ? 'active' : ''}`} onClick={() => setUnitMode('bultos')}>Bultos</button>
            <button className={`switch-toggle-btn ${unitMode === 'unidades' ? 'active' : ''}`} onClick={() => setUnitMode('unidades')}>Unidades</button>
          </div>
          <div className="date-filters-group">
            <MultiSelectFilter label="Mes" name="mes" icon={Calendar} options={nombresMeses} selectedValues={selectedMeses} onChange={(_, v) => setSelectedMeses(v)} />
            <MultiSelectFilter label="Año" name="anio" icon={Calendar} options={isMobile ? [new Date().getFullYear()] : [2024, 2025, 2026]} selectedValues={selectedAnios} onChange={(_, v) => setSelectedAnios(v)} />
          </div>
          <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
            <GlobalSearch 
              availableOptions={availableOptions}
              filterLabels={filterLabels}
              filters={filters}
              setFilters={setFilters}
            />
          </div>
        </div>
      </header>

      <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
        <div className="filters-sidebar-header">
          <h3>Filtros Disponibles</h3>
          <div className="filters-header-actions">
            <button className="reset-filters-btn" onClick={resetFilters} title="Restablecer filtros">
              <FilterX size={16} />
            </button>
            <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="filters-grid">
          {Object.keys(filters).map((key) => (
            <MultiSelectFilter key={key} label={filterLabels[key] || key} name={key} icon={key.includes('VENDEDOR') || key.includes('CLIENTE') ? Users : Tag} options={availableOptions[key]} selectedValues={filters[key]} onChange={handleFilterChange} />
          ))}
          <button className="filter-pill-button reset-filters" onClick={resetFilters}>
            <FilterX size={12} className="pill-icon" />
            <span className="pill-label">Limpiar</span>
          </button>
        </div>
      </div>
      {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

      <div className="dashboard-indicators">
        <div className="indicator-card v-real">
          <div className="card-content">
            <span className="indicator-label">Total Sell Out</span>
            <div className="indicator-row">
              <span className="indicator-value">{formatNumber(metrics.sellout)}</span>
              <div className="card-variations">
                <span className={`variation-item ${metrics.var_year >= 0 ? 'positive' : 'negative'}`}>
                  {metrics.var_year >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                  {Math.abs(metrics.var_year || 0).toFixed(1)}% Anual
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="indicator-card q-meta">
          <div className="card-content">
            <span className="indicator-label">Total Sell In</span>
            <div className="indicator-row">
              <span className="indicator-value">{formatNumber(metrics.sellin)}</span>
              <div className="card-variations">
                <span className={`variation-item ${metrics.var_month >= 0 ? 'positive' : 'negative'}`}>
                  {metrics.var_month >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                  {Math.abs(metrics.var_month || 0).toFixed(1)}% Mensual
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="indicator-card p-cumplimiento">
          <div className="card-content">
            <span className="indicator-label">% Efectividad SO/SI</span>
            <div className="indicator-row">
              <span className="indicator-value">{(metrics.efectividad || 0).toFixed(1)}%</span>
              <div className="card-variations">
                <span className={`variation-item ${metrics.var_efec_year >= 0 ? 'positive' : 'negative'}`}>
                  {metrics.var_efec_year >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                  {Math.abs(metrics.var_efec_year || 0).toFixed(1)}% Anual
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="charts-grid main-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
        <div className="chart-container">
          <h3 className="chart-title">Comparativo Sell In vs Sell Out ({selectedAnios[0]})</h3>
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart data={chartData} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" animationDuration={isMobile ? 0 : 400} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#fff' }} />
              <YAxis axisLine={false} tickLine={false} tickFormatter={formatK} tick={{ fontSize: 9, fill: '#fff' }} />
              <Tooltip content={<CustomChartTooltip />} />
              <Legend verticalAlign="bottom" height={16} iconType="circle" wrapperStyle={{ fontSize: '10px', backgroundColor: 'rgba(255, 255, 255, 0.85)', borderRadius: '12px', padding: '5px', width: '90%', margin: '0 auto', left: '5%', bottom: 0, display: 'flex', justifyContent: 'center', color: '#fff' }} />
              <Bar dataKey="SellOut" fill="#0068b3" radius={[2, 2, 0, 0]} barSize={15}>
                <LabelList dataKey="SellOut" position="top" formatter={formatK} style={{ fontSize: '9px', fontWeight: 'bold', fill: '#fff' }} />
              </Bar>
              <Bar dataKey="SellIn" fill="#00ba52" radius={[2, 2, 0, 0]} barSize={15}>
                <LabelList dataKey="SellIn" position="top" formatter={formatK} style={{ fontSize: '9px', fontWeight: 'bold', fill: '#fff' }} />
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3 className="chart-title">Participación Marcas Sell Out (%)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart animationDuration={isMobile ? 0 : 500}>
              <Pie
                data={pieData}
                cx="50%" cy="45%" innerRadius={60} outerRadius={85} paddingAngle={4} dataKey="value"
                labelLine={false} label={renderCustomizedLabel} animationDuration={500} animationEasing="ease-out"
              >
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth={0} />)}
              </Pie>
              <Tooltip formatter={(value) => formatNumber(value)} />
              <Legend verticalAlign="bottom" height={16} iconType="circle" wrapperStyle={{ fontSize: '7px', backgroundColor: 'rgba(255, 255, 255, 0.85)', borderRadius: '12px', padding: '5px', width: '90%', margin: '0 auto', left: '5%', bottom: 0, display: 'flex', justifyContent: 'center', color: '#fff' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="charts-grid bottom-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s', marginTop: '20px' }}>
        <div className="chart-container mini table-fixed-height" style={{ gridColumn: 'span 2', height: '450px' }}>
          <div className="table-header-container">
            <h3 className="chart-title">Desempeño Detallado por Cliente Principal, Marca y Producto</h3>
            <button className="export-table-btn" onClick={handleExportExcel}>
              <Download size={14} /> Exportar
            </button>
          </div>
          <div className="minimal-table-wrapper">
            <div className="table-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr' }}>
              <div className="col-name">Estructura Detalle</div>
              <div className="col-data">Sell In</div>
              <div className="col-data">Sell Out</div>
              <div className="col-data">% Efec.</div>
            </div>
            <div className="table-body">
              {tableData?.map((client, cIdx) => {
                const isCliExpanded = expandedClients[client.name];
                return (
                  <React.Fragment key={cIdx}>
                    <div className="table-row advisor-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr' }} onClick={() => setExpandedClients(prev => ({...prev, [client.name]: !isCliExpanded}))}>
                      <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {isCliExpanded ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                        {client.name}
                      </div>
                      <div className="col-data">{formatNumber(client.si)}</div>
                      <div className="col-data">{formatNumber(client.so)}</div>
                      <div className="col-data bold">{(client.efectividad || 0).toFixed(1)}%</div>
                    </div>
                    {isCliExpanded && client.brands?.map((brand, bIdx) => {
                      const brandKey = `${client.name}-${brand.name}`;
                      const isBrandExpanded = expandedBrands[brandKey];
                      return (
                        <React.Fragment key={bIdx}>
                          <div className="table-row client-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr', paddingLeft: '20px' }} onClick={() => setExpandedBrands(prev => ({...prev, [brandKey]: !isBrandExpanded}))}>
                            <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              {isBrandExpanded ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                              {brand.name}
                            </div>
                            <div className="col-data">{formatNumber(brand.si)}</div>
                            <div className="col-data">{formatNumber(brand.so)}</div>
                            <div className="col-data">{(brand.efectividad || 0).toFixed(1)}%</div>
                          </div>
                          {isBrandExpanded && brand.skus?.map((sku, sIdx) => (
                            <div className="table-row marca-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr', paddingLeft: '40px', fontSize: '0.7rem', opacity: 0.8 }} key={sIdx}>
                              <div className="col-name truncate">{sku.name}</div>
                              <div className="col-data">{formatNumber(sku.si)}</div>
                              <div className="col-data">{formatNumber(sku.so)}</div>
                              <div className="col-data">{(sku.efectividad || 0).toFixed(1)}%</div>
                            </div>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="table-footer" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr' }}>
              <div className="col-name">TOTALES</div>
              <div className="col-data">{formatNumber(metrics.sellin)}</div>
              <div className="col-data">{formatNumber(metrics.sellout)}</div>
              <div className="col-data bold">{(metrics.efectividad || 0).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      </div>

      <footer className="status-footer">
        {querying ? "SINCRONIZANDO..." : `VISTA ACTUAL: ${selectedMeses.join(', ')} - ${selectedAnios.join(', ')}`}
      </footer>
    </motion.div>
  );
};

export default SellOutTradicional;